/**
 * Validation service for Certificate Search forms
 */

import type { FormData, SearchMode, ValidationError } from '../types';
import { DomUtils } from '../utils/DomUtils';

export class ValidationService {
    /**
     * Validate certificate number
     */
    static validateCertificateNumber(certificateNumber: string, translations: any = {}): ValidationError[] {
        const errors: ValidationError[] = [];
        const trimmed = DomUtils.trim(certificateNumber);

        if (trimmed.length === 0) {
            errors.push({
                field: 'certificatenr',
                message: translations['error.certificate_number.required'] || 'Certificate number is required',
                key: 'error.certificate_number.required',
            });
        } else if (trimmed.length < 11) {
            errors.push({
                field: 'certificatenr',
                message: translations['error.certificate_number.min_length'] || 'Certificate number must contain at least 11 characters',
                key: 'error.certificate_number.min_length',
            });
        }

        return errors;
    }

    /**
     * Validate advanced search fields
     */
    static validateAdvancedSearch(formData: FormData, translations: any = {}): ValidationError[] {
        const errors: ValidationError[] = [];

        const company = DomUtils.trim(formData.company);
        const norm = DomUtils.trim(formData.norm);
        const country = DomUtils.trim(formData.country);

        // Company is required
        if (company.length === 0) {
            errors.push({
                field: 'company',
                message: translations['error.company.required'] || 'Company field is required',
                key: 'error.company.required',
            });
        }

        // Norm is required
        if (norm.length === 0) {
            errors.push({
                field: 'norm',
                message: translations['error.norm.required'] || 'Norm field is required',
                key: 'error.norm.required',
            });
        }

        // Country must be exactly 2 characters or empty
        if (country.length !== 0 && country.length !== 2) {
            errors.push({
                field: 'country',
                message: translations['error.country.format'] || 'Country code must be exactly 2 characters',
                key: 'error.country.format',
            });
        }

        return errors;
    }

    /**
     * Validate Czech NDT search fields (certificate number + person name, both required).
     * Mirrors the former v9 extension tuev_nord_qwcertificatesearch.
     */
    static validateCzechNdtSearch(formData: FormData, translations: any = {}): ValidationError[] {
        const errors: ValidationError[] = [];

        if (DomUtils.trim(formData.certificatenr).length === 0) {
            errors.push({
                field: 'certificatenr',
                message: translations['error.certificate_number.required'] || 'Certificate number is required',
                key: 'error.certificate_number.required',
            });
        }

        if (DomUtils.trim(formData.person).length === 0) {
            errors.push({
                field: 'person',
                message: translations['error.person_name.required'] || 'Name of the certified person is required',
                key: 'error.person_name.required',
            });
        }

        return errors;
    }

    /**
     * Validate all form data based on search mode
     */
    static validateForm(formData: FormData, searchMode: SearchMode, translations: any = {}): ValidationError[] {
        if (searchMode === 'czechNdt') {
            return this.validateCzechNdtSearch(formData, translations);
        }
        if (searchMode === 'certificate') {
            return this.validateCertificateNumber(formData.certificatenr, translations);
        }
        return this.validateAdvancedSearch(formData, translations);
    }

    /**
     * Get error messages for specific field
     */
    static getFieldErrors(errors: ValidationError[], field: string): ValidationError[] {
        return errors.filter((error) => error.field === field);
    }

    /**
     * Check if validation has errors
     */
    static hasErrors(errors: ValidationError[]): boolean {
        return errors.length > 0;
    }

    /**
     * Group errors by field
     */
    static groupErrorsByField(errors: ValidationError[]): Record<string, ValidationError[]> {
        return errors.reduce(
            (grouped, error) => {
                const field = error.field;
                if (!grouped[field]) {
                    grouped[field] = [];
                }
                grouped[field].push(error);
                return grouped;
            },
            {} as Record<string, ValidationError[]>
        );
    }
}
